/**
 * « Ajouter à mon agenda » (SPEC FC13, proposition) : fichier .ics généré dans le navigateur, sans
 * appel réseau. Heures Mindbody = heure locale du site, sans fuseau : l'événement est écrit en
 * Europe/Paris (fuseau de tous les sites Monday), avec sa définition VTIMEZONE pour les agendas
 * stricts.
 */

export interface IcsEvent {
  /** Identifiant stable (même cours = même événement si on l'ajoute deux fois). */
  uid: string;
  /** `StartDateTime` / `EndDateTime` Mindbody, ex. 2026-10-05T07:45:00 (heure locale du site). */
  start: string;
  end?: string | null;
  summary: string;
  location?: string;
  description?: string;
  url?: string;
  /** Horodatage de création (tests) ; défaut : maintenant. */
  now?: Date;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** '2026-10-05T07:45:00' → '20261005T074500' (heure murale, sans conversion de fuseau). */
export function icsLocal(dateTime: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(dateTime);
  if (!m) throw new Error(`Date Mindbody illisible\u00a0: ${dateTime}`);
  return `${m[1]}${m[2]}${m[3]}T${m[4]}${m[5]}${m[6] ?? '00'}`;
}

function icsUtc(date: Date): string {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(
    date.getUTCMinutes(),
  )}${pad(date.getUTCSeconds())}Z`;
}

/** Échappement RFC 5545 des valeurs texte. */
export function icsEscape(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/([,;])/g, '\\$1');
}

/** Pliage RFC 5545 : lignes de 75 octets au plus, suite précédée d'une espace. */
export function icsFold(line: string): string {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= 75) return line;
  const out: string[] = [];
  let current = '';
  let size = 0;
  for (const char of line) {
    const n = new TextEncoder().encode(char).length;
    const limit = out.length === 0 ? 75 : 74;
    if (size + n > limit) {
      out.push(current);
      current = '';
      size = 0;
    }
    current += char;
    size += n;
  }
  out.push(current);
  return out.join('\r\n ');
}

const VTIMEZONE_PARIS = [
  'BEGIN:VTIMEZONE',
  'TZID:Europe/Paris',
  'BEGIN:DAYLIGHT',
  'TZOFFSETFROM:+0100',
  'TZOFFSETTO:+0200',
  'TZNAME:CEST',
  'DTSTART:19700329T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
  'END:DAYLIGHT',
  'BEGIN:STANDARD',
  'TZOFFSETFROM:+0200',
  'TZOFFSETTO:+0100',
  'TZNAME:CET',
  'DTSTART:19701025T030000',
  'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
  'END:STANDARD',
  'END:VTIMEZONE',
];

export function buildIcs(event: IcsEvent): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Monday Sports Club//Reservation//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...VTIMEZONE_PARIS,
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `DTSTAMP:${icsUtc(event.now ?? new Date())}`,
    `DTSTART;TZID=Europe/Paris:${icsLocal(event.start)}`,
  ];
  if (event.end) lines.push(`DTEND;TZID=Europe/Paris:${icsLocal(event.end)}`);
  lines.push(`SUMMARY:${icsEscape(event.summary)}`);
  if (event.location) lines.push(`LOCATION:${icsEscape(event.location)}`);
  if (event.description) lines.push(`DESCRIPTION:${icsEscape(event.description)}`);
  if (event.url) lines.push(`URL:${event.url}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.map(icsFold).join('\r\n') + '\r\n';
}

/** Nom de fichier lisible : « dynamo-signature-2026-10-05.ics ». */
export function icsFileName(summary: string, start: string): string {
  const slug = summary
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'cours'}-${start.slice(0, 10)}.ics`;
}

/** Télécharge le fichier dans le navigateur (aucun appel réseau). */
export function downloadIcs(event: IcsEvent): void {
  const blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = icsFileName(event.summary, event.start);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
